'use client';

import Image from 'next/image';
import Link from 'next/link';
import Accordion from '@/components/shared/Accordion/Accordion';
import type { PageContent } from '@/services/api/page-contents';
import { getImageUrl } from '@/utils/api-utils';

interface FAQSectionProps {
  content?: PageContent | null;
}

type DaiHocFAQContent = {
  type?: string;
  title?: string;
  subtitle1?: string;
  subtitle2?: string;
  button?: { label?: string; href?: string };
  image?: string;
  faqs?: Array<{ title?: string; content?: string }>;
};

const isDaiHocFAQContent = (value: unknown): value is DaiHocFAQContent => {
  return !!value && typeof value === 'object' && (value as DaiHocFAQContent).type === 'dai-hoc-faq-section';
};

const fallbackFaqs = [
  {
    id: 1,
    title: 'Về Tuyển sinh – Đại học, Thạc sĩ, Tiến sĩ?',
    content: 'HUB University tuyển sinh các hệ đại học chính quy, vừa làm vừa học, liên kết quốc tế cùng các chương trình sau đại học (Thạc sĩ, Tiến sĩ). Thí sinh có thể đăng ký xét tuyển qua điểm thi tốt nghiệp THPT, học bạ hoặc kết quả thi riêng của trường.',
  },
  {
    id: 2,
    title: 'Về Chương trình đào tạo?',
    content: 'HUB University cung cấp các chương trình đào tạo đa dạng trong lĩnh vực Ngân hàng, Tài chính, Kế toán và Quản trị Kinh doanh. Chương trình học được thiết kế theo chuẩn quốc tế, kết hợp lý thuyết và thực hành.',
  },
  {
    id: 3,
    title: 'Học phí – Học bổng?',
    content: 'Học phí tại HUB University dao động từ 18-25 triệu đồng/năm tùy theo ngành học và hệ đào tạo. Trường có nhiều chương trình học bổng hấp dẫn từ 30-100% học phí.',
  },
  {
    id: 4,
    title: 'Cơ hội việc làm – Thực tập?',
    content: 'HUB University có mạng lưới đối tác rộng khắp với hơn 200 ngân hàng, công ty tài chính và doanh nghiệp hàng đầu. Tỷ lệ sinh viên có việc làm sau tốt nghiệp đạt trên 95%.',
  },
  {
    id: 5,
    title: 'Cơ sở vật chất – Sinh hoạt sinh viên?',
    content: 'Trường có 3 cơ sở tại TP.HCM với hệ thống phòng học hiện đại, phòng thí nghiệm ngân hàng mô phỏng, thư viện điện tử với hơn 50,000 đầu sách.',
  },
  {
    id: 6,
    title: 'Học chuyển tiếp – Quốc tế hóa?',
    content: 'HUB University có chương trình liên kết với nhiều trường đại học danh tiếng tại Úc, Mỹ, Anh, Singapore. Sinh viên có thể chuyển tiếp sang các trường đối tác để lấy bằng quốc tế.',
  },
];

export default function FAQSection({ content }: FAQSectionProps) {
  const resolved = (() => {
    const raw = content?.content;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return isDaiHocFAQContent(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return isDaiHocFAQContent(raw) ? raw : null;
  })();

  const title = resolved?.title || 'CÂU HỎI THƯỜNG GẶP';
  const subtitle1 = resolved?.subtitle1 || 'HUB - Nơi hội tụ tinh hoa tài chính';
  const subtitle2 = resolved?.subtitle2 || 'Dẫn đầu giáo dục tài chính - Ngân hàng';
  const shouldShowViewAll = !!resolved?.button?.href || !!resolved?.button?.label;
  const viewAllLabel = resolved?.button?.label || 'XEM TẤT CẢ';
  const viewAllHref = resolved?.button?.href || '/faq';
  const image = resolved?.image || '/assets/images/cauhoi_img1.png';

  const faqs = (() => {
    const configured = Array.isArray(resolved?.faqs) ? resolved?.faqs : null;
    if (!configured || configured.length === 0) return fallbackFaqs;
    const mapped = configured
      .map((item, index) => {
        const itemTitle = typeof item?.title === 'string' ? item.title.trim() : '';
        const itemContent = typeof item?.content === 'string' ? item.content.trim() : '';
        if (!itemTitle || !itemContent) return null;
        return { id: index + 1, title: itemTitle, content: itemContent };
      })
      .filter(Boolean) as typeof fallbackFaqs;
    return mapped.length > 0 ? mapped : fallbackFaqs;
  })();

  return (
    <section
      className="faq-section section-spacing"
      id="faq"
      aria-labelledby="faq-heading"
    >
      <div className="container">
        <div className="row">
          <div className="col-lg-5 mb-4 mb-lg-0">
            <Image
              src={getImageUrl(image) || image}
              alt="Sinh viên HUB University"
              className="faq-section__image d-none d-sm-block"
              width={452}
              height={520}
              loading="lazy"
            />
          </div>
          <div className="col-lg-7">
            <h2
              id="faq-heading"
              className="section-title faq-section__heading-main d-none d-sm-block"
            >
              {title}
            </h2>
            <p className="faq-section__subtitle faq-section__subtitle--line d-none d-sm-block">
              {subtitle1}
            </p>
            <p className="faq-section__subtitle faq-section__subtitle--line d-none d-sm-block">
              {subtitle2}
            </p>

            <div
              className="mobile-title-box mobile-title-box--lg d-sm-none"
              aria-hidden="true"
            >
              <div className="mobile-title-box__icon">
                <Image
                  src="/assets/svg/question.svg"
                  alt=""
                  width={24}
                  height={24}
                  aria-hidden="true"
                />
              </div>
              <div className="mobile-title-box__content">
                <p className="mobile-title-box__overline">TRƯỜNG ĐẠI HỌC NGÂN HÀNG HUB</p>
                <h2 className="mobile-title-box__title">{title}</h2>
              </div>
            </div>

            <Accordion items={faqs} variant="faq" />

            {shouldShowViewAll ? (
              <Link href={viewAllHref} className="faq-section__view-all">
                <span>{viewAllLabel}</span>
                <span className="iconify faq-section__view-all-icon" data-icon="mdi:arrow-right"></span>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
